import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Container, Row, Col, Card, CardBody, FormGroup, Label, Button, Alert } from 'reactstrap'

import { Breadcrumbs } from '../../AbstractElements'
import { useLayout } from '../../Hooks/useLayout'
import { useRoleBasedUrl } from '../../Hooks/useRoleBasedUrl'

import { getIdData, addData, updateData } from '../../lib/parameters'
import { ParameterTypeSelect } from '../Common/FormSelects'

const AdminParameterEdit = () => {
	const layout = useLayout()
	const role = useRoleBasedUrl()
	const { id } = useParams()
	const navigate = useNavigate()
	const [formData, setFormData] = useState({
		type: '',
		pname: '',
		order: 0,
		isActive: true
	})
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')

	const isAddMode = !id || id === 'create'

	useEffect(() => {
		if (!isAddMode) {
			const fetchParameterData = async () => {
				try {
					const parameterData = await getIdData(id)
					const parameter = parameterData[0]
					setFormData({
						type: parameter.type,
						pname: parameter.pname,
						order: parameter.order,
						isActive: parameter.isActive
					})
				} catch (err) {
					setError('Failed to load parameter data')
					console.error('Error fetching parameter data:', err)
				}
			}
			fetchParameterData()
		}
	}, [id, isAddMode])

	const handleInputChange = (e) => {
		const { name, value, type, checked } = e.target
		setFormData((prev) => ({
			...prev,
			[name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) || 0 : value
		}))
	}

	const handleTypeChange = (value) => {
		setFormData((prev) => ({
			...prev,
			type: value
		}))
	}

	const handleSubmit = async (e) => {
		e.preventDefault()
		setLoading(true)
		setError('')

		try {
			const parameterData = isAddMode
				? {
						pname: formData.pname,
						type: formData.type,
						order: formData.order,
						isActive: formData.isActive
				  }
				: {
						id,
						pname: formData.pname,
						type: formData.type,
						order: formData.order,
						isActive: formData.isActive
				  }

			if (isAddMode) {
				await addData(parameterData)
			} else {
				await updateData(parameterData)
			}

			navigate(`${process.env.PUBLIC_URL}/${role}/parameters/${layout}`)
		} catch (err) {
			setError(err.message || 'Failed to save parameter')
			console.error('Error saving parameter:', err)
		} finally {
			setLoading(false)
		}
	}

	const handleCancel = () => {
		navigate(`${process.env.PUBLIC_URL}/${role}/parameters/${layout}`)
	}

	return (
		<Container fluid>
			<Breadcrumbs
				title={isAddMode ? 'Add Parameter' : 'Update Parameter'}
				mainTitle={isAddMode ? 'Add Parameter' : 'Update Parameter'}
				parent="Parameters"
				url={`${process.env.PUBLIC_URL}/${role}/parameters/${layout}`}
			/>

			<Row>
				<Col sm="12">
					<Card>
						<CardBody>
							<div className="personal-info-head mb-5">
								<h4>{isAddMode ? 'Add Parameter' : 'Update Parameter'}</h4>
								<p>Manage system parameters and configuration</p>
							</div>

							{error && (
								<Alert color="danger" className="mb-4">
									{error}
								</Alert>
							)}

							<form onSubmit={handleSubmit}>
								<Row>
									<Col md="6">
										<FormGroup>
											<Label className="form-control-label">Type</Label>
											<ParameterTypeSelect value={formData.type} setValue={handleTypeChange} />
										</FormGroup>
									</Col>
									<Col md="6">
										<FormGroup>
											<Label className="form-control-label">Parameter Name</Label>
											<input
												type="text"
												name="pname"
												className="form-control"
												value={formData.pname}
												onChange={handleInputChange}
												placeholder="Enter parameter name"
												required
											/>
										</FormGroup>
									</Col>
								</Row>

								<Row>
									<Col md="6">
										<FormGroup>
											<Label className="form-control-label">Order</Label>
											<input
												type="number"
												name="order"
												className="form-control"
												value={formData.order}
												onChange={handleInputChange}
												placeholder="Enter order number"
												min="0"
											/>
										</FormGroup>
									</Col>
									<Col md="6">
										<FormGroup>
											<Label className="form-control-label">Is Active</Label>
											<div className="form-check form-switch">
												<input
													className="form-check-input"
													type="checkbox"
													name="isActive"
													checked={formData.isActive}
													onChange={handleInputChange}
												/>
												<Label className="form-check-label">{formData.isActive ? 'Active' : 'Inactive'}</Label>
											</div>
										</FormGroup>
									</Col>
								</Row>

								<div className="d-flex justify-content-between mt-4">
									<Button type="submit" color="primary" disabled={loading}>
										{loading ? 'Saving...' : isAddMode ? 'Add Parameter' : 'Update Parameter'}
									</Button>
									<Button type="button" color="secondary" onClick={handleCancel} disabled={loading}>
										Cancel
									</Button>
								</div>
							</form>
						</CardBody>
					</Card>
				</Col>
			</Row>
		</Container>
	)
}

export { AdminParameterEdit }
